import CustomTooltip from "./CustomTooltip";

export interface CustomToggleButtonProps {
  id: string;
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
  toolTip?: string;
}

export interface CustomToggleButtonGroupProps {
  id: string;
  buttonOptions?: CustomToggleButtonProps[];
  selectedButton: string | null;
  size: "sm" | "md" | "sm xl1440:text-md" | "xs xl1440:text-sm";
  onClick?: (buttonLabel: string) => void;
  buttonWidth?: string;
}

const ToggleButtonGroup = (props: CustomToggleButtonGroupProps) => {
  const handleClick = (buttonSource: string) =>
    props.onClick && props.onClick(buttonSource);

  return (
    <div className="inline-flex" id={`${props.id}`}>
      {props.buttonOptions?.map(
        (toggleButtonProps: CustomToggleButtonProps, optionIndex: number) => {
          const isSelected =
            toggleButtonProps.isActive &&
            props.selectedButton === toggleButtonProps.label;

          /*  layout */
          const baseStyle =
            `normal-case flex h-[2.5rem] px-4 py-2 justify-center items-center gap-2 bg-base-white z-0 ${props.buttonWidth ? "" : "min-w-[5rem]"} `;

          /*  borders */
          let borderStyle = "border-solid ";
          borderStyle += isSelected
            ? "border-sunrise "
            : "border-primary-gray-light-300 ";
          borderStyle += "border-[1px] ";

          if (optionIndex === 0) {
            borderStyle += "rounded-l-xl ";
          } else {
            borderStyle += "!border-l-[0] ";
          }

          if (optionIndex === (props.buttonOptions?.length ?? 0) - 1)
            borderStyle += "rounded-r-xl ";

          /*  font    */
          const fontStyle = `[word-break:keep-all] whitespace-nowrap text-${props.size
            } ${isSelected
              ? "text-primary-gray-light-800 "
              : toggleButtonProps.isActive
              ? "text-primary-gray-light-800 "
              : "text-primary-gray-light-400 "
          }`;

          let styleOverrides = baseStyle + borderStyle + fontStyle;

          /*  activated state */
          if (isSelected && toggleButtonProps.isActive) {
            styleOverrides += "border-x-[1px] border-r-blossom bg-sunrise ";
          } else {
            styleOverrides += "bg-base-white ";
          }

          /*  disabled state */
          if (!toggleButtonProps.isActive) {
            styleOverrides += "cursor-not-allowed opacity-40 ";
          }

          /*  focused state */
          styleOverrides +=
            "focus:z-50 focus:outline-none focus:[box-shadow:0px_0px_2px_2px_rgba(255,_164,_0,_0.24)] ";

          /*  hovered state */
          if (toggleButtonProps.isActive) {
            styleOverrides +=
              "hover:z-50 hover:outline-none hover:[box-shadow:0px_0px_2px_2px_rgba(255,_164,_0,_0.24)] ";
          }

          return (
            <div
              key={`key-${optionIndex}`}
              id={`${props.id}-${toggleButtonProps.id}-togglebutton`}
            >
              <CustomTooltip
                id={`${toggleButtonProps.id}-togglebutton`}
                title={toggleButtonProps.toolTip}
                placement="top-start"
                arrow
                disableHoverListener={!toggleButtonProps.toolTip}
              >
                <span>
                  <button
                    id={`${toggleButtonProps.id}-button`}
                    className={`${styleOverrides}`}
                    style={props.buttonWidth ? { width: props.buttonWidth } : undefined}
                    disabled={!toggleButtonProps.isActive}
                    // US 27515 AC 13a: the selected option must be programmatically identifiable —
                    // the selected state was CSS-only, so assistive technology announced every
                    // option identically. Same pattern US 25479 uses on the Renewals CRM segments.
                    aria-pressed={isSelected}
                    onClick={() => handleClick(toggleButtonProps.label)}
                  >
                    {toggleButtonProps.icon ?? toggleButtonProps.label}
                  </button>
                </span>
              </CustomTooltip>
            </div>
          );
        }
      )}
    </div>
  );
};

export default ToggleButtonGroup;
